import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { PasswordInput, PasswordValidationTypography, SimpleModal, toast } from '@livable/saas-react-components';
import { track, events } from 'src/configs/amplitude';
import { Button, Checkbox, FormControlLabel, Grid, FormGroup, Link, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useStore } from 'src/stores';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import PrivacyPolicy from 'src/components/general/PrivacyPolicy';
import { resetPasswordSetInitialValues, resetPasswordSetSchema } from 'src/utils/formValidation/ResetPassword';
import { beforeSubmitDataValidation } from 'src/core/services/beforeSubmitDataValidation';
import SplitPageContainer from 'src/components/shared/SplitPageContainer';
import LoginLeftContent from 'src/components/shared/LoginLeftContent';
import { PASSWORD_REQUIRED_ERROR, PASSWORD_RULES_ERROR } from 'src/utils/formValidation/SignUp';
import { api } from 'src/api';
import mobileLoginSvg from 'src/img/mobile-login.svg';
import useStyles from './styles';
import { IS_PAYMENT_METHOD } from 'src/constants/payments';
import { INVITE_LINK_INVALID_ERROR_CODE, readInviteLink, removeInviteCredentialsFromUrl } from 'src/utils/inviteLink';
export const ResetPasswordSetPage = observer(() => {
  const store = useStore();
  const classes = useStyles();
  const navigate = useNavigate();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const page = 'Set Password';
  const isLoggedIn = api.Account.isLoggedIn();
  const searchParams = new URLSearchParams(window.location.search);
  const isAppfolio = searchParams.get('isappfolio');
  const appfolioEmail = searchParams.get('appfolioemail');
  // Read once: the invite credentials are removed from the address bar after the first render.
  const _useState = useState(() => readInviteLink(window.location.search)),
    _useState2 = _slicedToArray(_useState, 1),
    inviteLink = _useState2[0];
  const inviteToken = inviteLink.inviteToken,
    legacyUsername = inviteLink.legacyUsername,
    legacyTempPassword = inviteLink.legacyTempPassword,
    isInvitedUser = inviteLink.isInviteLink;
  const _useState3 = useState(!!isLoggedIn),
    _useState4 = _slicedToArray(_useState3, 2),
    isActiveSession = _useState4[0],
    setIsActiveSession = _useState4[1];
  const _useState5 = useState(!isInvitedUser),
    _useState6 = _slicedToArray(_useState5, 2),
    termsAndConditionModalState = _useState6[0],
    setTermsAndConditionModalState = _useState6[1];
  const _useState7 = useState(!isInvitedUser),
    _useState8 = _slicedToArray(_useState7, 2),
    termAndConditionChecked = _useState8[0],
    setTermAndConditionChecked = _useState8[1];
  const _useState9 = useState(!isInvitedUser),
    _useState0 = _slicedToArray(_useState9, 2),
    privacyPolicyModalState = _useState0[0],
    setPrivacyPolicyModalState = _useState0[1];
  const _useState1 = useState(!isInvitedUser),
    _useState10 = _slicedToArray(_useState1, 2),
    privacyPolicyChecked = _useState10[0],
    setPrivacyPolicyChecked = _useState10[1];
  const _useState11 = useState(!isInvitedUser),
    _useState12 = _slicedToArray(_useState11, 2),
    userAcknowledgmentModalState = _useState12[0],
    setUserAcknowledgmentModalState = _useState12[1];
  const _useState13 = useState(!isInvitedUser),
    _useState14 = _slicedToArray(_useState13, 2),
    userAcknowledgmentChecked = _useState14[0],
    setUserAcknowledgmentChecked = _useState14[1];
  const formik = useFormik({
    initialValues: resetPasswordSetInitialValues,
    validationSchema: resetPasswordSetSchema,
    onSubmit: async values => {
      let invitePasswordData = null;
      if (inviteToken) {
        invitePasswordData = {
          token: inviteToken,
          password: values.password
        };
      } else if (isInvitedUser) {
        // a link sent before LB-6162: log in with the temporary password it carries first
        const invitedUserResp = await store.account.loginInviteAsync.run({
          userName: legacyUsername,
          password: legacyTempPassword
        });
        if (!invitedUserResp || !invitedUserResp.isSuccessful) {
          navigate('/');
          return;
        }
        invitePasswordData = {
          userName: legacyUsername,
          password: values.password,
          session: invitedUserResp.response.session
        };
      }
      if (invitePasswordData) {
        var _store$account$change;
        const resp = await store.account.changeTemporaryInvitePasswordV2Async.run(beforeSubmitDataValidation(invitePasswordData));
        if (!(resp !== null && resp !== void 0 && resp.isSuccessful) && ((_store$account$change = store.account.changeTemporaryInvitePasswordV2Async.error) === null || _store$account$change === void 0 ? void 0 : _store$account$change.errorCode) === INVITE_LINK_INVALID_ERROR_CODE) {
          navigate('/');
          return;
        }
        if (resp && resp.isSuccessful) {
          toast('success', {
            body: ['Password successfully set. Have a good time.']
          });
          await store.account.getProfileAsync.run();
          await store.company.getCurrentUserCompanyFromTokenAsync.run();

          // Get existing portfolio only if user not an admin
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          !(store.account.userProfile.isUserAdmin || store.account.userProfile.isUserSuperAdmin) && (await store.portfolios.getExistingPortfoliosAsync.run());
          if (store.account.userProfile.isUserAdmin || store.account.userProfile.isUserSuperAdmin) {
            toast('success', {
              body: ['Please select company to continue work!']
            });
          }
          if (store.company.companyFeatureStore.isAppfolio) {
            localStorage.setItem(IS_PAYMENT_METHOD, 'true');
            navigate('/active_dashboard?isappfolio=true');
            return;
          }
          navigate(store.account.userProfile.isUserAdmin || store.account.userProfile.isUserSuperAdmin ? '/admin' : '/');
          return;
        }
        return;
      }
      if (!!localStorage.getItem('INVITED_USER_EMAIL')) {
        const data = {
          email: localStorage.getItem('INVITED_USER_EMAIL'),
          password: values.password,
          session: localStorage.getItem('INVITED_USER_SESSION')
        };
        const resp = await store.account.changeTemporaryPasswordAsync.run(beforeSubmitDataValidation(data));
        if (resp && resp.isSuccessful) {
          localStorage.removeItem('INVITED_USER_EMAIL');
          localStorage.removeItem('INVITED_USER_SESSION');
          navigate('/password_success');
        }
      } else {
        const data = {
          email: searchParams.get('email'),
          password: values.password,
          confirmationCode: searchParams.get('confirmationCode')
        };
        const resp = await store.account.resetAccountPasswordAsync.run(beforeSubmitDataValidation(data));
        if (resp && resp.isSuccessful) {
          navigate('/password_success');
        }
      }
    }
  });
  const setUserNewPassword = async () => {
    const logoutResp = await store.account.logoutAccountAsync.run();
    if (!logoutResp || !logoutResp.isSuccessful) {
      store.auth.reset();
    }
    setIsActiveSession(false);
  };
  useEffect(() => {
    Promise.all([formik.setFieldValue('termsAndConditions', termAndConditionChecked), formik.setFieldValue('privacyPolicy', privacyPolicyChecked), formik.setFieldValue('userAcknowledgment', userAcknowledgmentChecked)]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsAndConditionModalState, termAndConditionChecked, privacyPolicyModalState, privacyPolicyChecked, userAcknowledgmentModalState, userAcknowledgmentChecked, formik.isValid]);
  useEffect(() => {
    if (!(isInvitedUser || localStorage.getItem('INVITED_USER_EMAIL') && localStorage.getItem('INVITED_USER_SESSION') || searchParams.get('email') && searchParams.get('confirmationCode'))) {
      navigate('/');
      return;
    }
    removeInviteCredentialsFromUrl();
    if (isInvitedUser) {
      setTimeout(() => {
        toast('success', {
          body: ['Welcome to Livable, the last step remains.']
        });
      }, 1250);
    }
    return () => {
      localStorage.removeItem('INVITED_USER_EMAIL');
      localStorage.removeItem('INVITED_USER_SESSION');
      localStorage.removeItem('INVITED_USER_USERNAME');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isActiveSession && /*#__PURE__*/React.createElement(SplitPageContainer, {
    largeTopMargin: true,
    leftBlock: /*#__PURE__*/React.createElement(LoginLeftContent, null),
    imageScr: mobileLoginSvg
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3",
    marginBottom: "40px"
  }, "You are trying to continue as a different user, this will log you out of the current session, do you agree to this?"), /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%'
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    fullWidth: true,
    onClick: setUserNewPassword,
    'custom-attribute': '801-reset-password-set-page'
  }, "Yes")), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    fullWidth: true,
    component: RouterLink,
    to: "/",
    'custom-attribute': '802-reset-password-set-page'
  }, "No"))))), !isActiveSession && /*#__PURE__*/React.createElement(SplitPageContainer, {
    largeTopMargin: true,
    leftBlock: /*#__PURE__*/React.createElement(LoginLeftContent, null),
    imageScr: mobileLoginSvg,
    isAppfolio: isAppfolio
  }, !isInvitedUser && /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('sign_up_flow.set_new_password')), isInvitedUser && !isAppfolio && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('sign_up_flow.welcome_to_livable')), /*#__PURE__*/React.createElement("div", {
    className: classes.xsSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('sign_up_flow.we_are_glad_to_see_you')))), isInvitedUser && isAppfolio && /*#__PURE__*/React.createElement(React.Fragment, null, appfolioEmail && /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, "Hello, ".concat(appfolioEmail)), /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('Please finish the sign up')), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('Please create password'))), /*#__PURE__*/React.createElement("div", {
    className: classes.pageForm
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: formik.handleSubmit
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement(PasswordValidationTypography, {
    title: t('errors.password_validation_length'),
    isValidated: formik.touched.password,
    isInvalid: formik.errors.password === PASSWORD_REQUIRED_ERROR || formik.values.password.trim().length < 8
  }), /*#__PURE__*/React.createElement(PasswordValidationTypography, {
    title: t('errors.password_validation_chars'),
    isValidated: formik.touched.password,
    isInvalid: formik.errors.password === PASSWORD_REQUIRED_ERROR || formik.errors.password === PASSWORD_RULES_ERROR
  }), /*#__PURE__*/React.createElement(PasswordValidationTypography, {
    gutterBottom: true,
    title: t('errors.password_validation_match'),
    isValidated: formik.touched.repPassword,
    isInvalid: !!formik.errors.repPassword
  })), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(PasswordInput, {
    id: "password",
    name: "password",
    label: t('profile_settings.new_password'),
    value: formik.values.password,
    onBlur: e => {
      if (e.target.value) {
        formik.handleBlur(e);
        track(events.filled('New Password'), {
          page
        });
      }
    },
    onChange: formik.handleChange,
    error: formik.touched.password && !!formik.errors.password
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(PasswordInput, {
    id: "repPassword",
    name: "repPassword",
    label: t('profile_settings.repeat_new_password'),
    value: formik.values.repPassword,
    onBlur: e => {
      if (e.target.value) {
        formik.handleBlur(e);
        track(events.filled('Repeat New Password'), {
          page
        });
      }
    },
    onChange: formik.handleChange,
    error: formik.touched.repPassword && !!formik.errors.repPassword
  })), !isInvitedUser ? null : /*#__PURE__*/React.createElement(FormGroup, {
    className: classes.checkboxActions
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      id: "termsAndConditions",
      name: "termsAndConditions",
      checked: termAndConditionChecked,
      onChange: () => termAndConditionChecked ? setTermAndConditionChecked(false) : setTermsAndConditionModalState(true)
    }),
    label: /*#__PURE__*/React.createElement("span", {
      className: classes.termsConditionsLine
    }, t('general.i_agree_with'), ' ', /*#__PURE__*/React.createElement(SimpleModal, {
      trigger: /*#__PURE__*/React.createElement(Link, {
        href: "#"
      }, t('general.terms_and_conditions')),
      handleOpen: () => {
        setTermsAndConditionModalState(true);
      },
      handleClose: () => {
        setTermsAndConditionModalState(false);
      },
      isOpen: termsAndConditionModalState,
      title: t('general.terms_and_conditions'),
      noPadding: true
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.modalScrollableContent
    }, /*#__PURE__*/React.createElement(PrivacyPolicy, null), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      id: "acceptTermsButton",
      fullWidth: true,
      onClick: () => {
        setTermAndConditionChecked(true);
        setTermsAndConditionModalState(false);
      },
      'custom-attribute': '803-reset-password-set-page'
    }, t('general.terms_and_conditions_accept')))))
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      id: "privacyPolicy",
      name: "privacyPolicy",
      checked: privacyPolicyChecked,
      onChange: () => privacyPolicyChecked ? setPrivacyPolicyChecked(false) : setPrivacyPolicyModalState(true)
    }),
    label: /*#__PURE__*/React.createElement("span", {
      className: classes.termsConditionsLine
    }, t('general.i_agree_with'), ' ', /*#__PURE__*/React.createElement(SimpleModal, {
      trigger: /*#__PURE__*/React.createElement(Link, {
        href: "#"
      }, t('general.privacy_policy')),
      isOpen: privacyPolicyModalState,
      handleOpen: () => {
        setPrivacyPolicyModalState(true);
      },
      handleClose: () => {
        setPrivacyPolicyModalState(false);
      },
      title: t('general.privacy_policy'),
      noPadding: true
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.modalScrollableContent
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, t('general.privacy_policy_body')), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      id: "acceptPrivacyButton",
      fullWidth: true,
      onClick: () => {
        setPrivacyPolicyChecked(true);
        setPrivacyPolicyModalState(false);
      },
      'custom-attribute': '804-reset-password-set-page'
    }, t('general.privacy_policy_accept')))), ' ')
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      id: "userAcknowledgment",
      name: "userAcknowledgment",
      checked: userAcknowledgmentChecked,
      onChange: () => userAcknowledgmentChecked ? setUserAcknowledgmentChecked(false) : setUserAcknowledgmentModalState(true)
    }),
    label: /*#__PURE__*/React.createElement("span", {
      className: classes.termsConditionsLine
    }, t('general.i_agree_with'), ' ', /*#__PURE__*/React.createElement(SimpleModal, {
      trigger: /*#__PURE__*/React.createElement(Link, {
        href: "#"
      }, t('general.user_acknowledgment')),
      isOpen: userAcknowledgmentModalState,
      handleOpen: () => {
        setUserAcknowledgmentModalState(true);
      },
      handleClose: () => {
        setUserAcknowledgmentModalState(false);
      },
      title: t('general.user_acknowledgment'),
      noPadding: true
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.modalScrollableContent
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, t('general.user_acknowledgment_body')), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      id: "acceptWarningButton",
      fullWidth: true,
      onClick: () => {
        setUserAcknowledgmentChecked(true);
        setUserAcknowledgmentModalState(false);
      },
      'custom-attribute': '805-reset-password-set-page'
    }, t('general.user_acknowledgment_accept')))))
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    fullWidth: true,
    onClick: () => track(events.clicked('Save New Password'), {
      page
    }),
    disabled: !formik.isValid || !Object.keys(formik.touched).length || store.account.loginAccountAsync.inProgress || store.account.resetAccountPasswordAsync.inProgress || store.account.changeTemporaryPasswordAsync.inProgress || store.account.changeTemporaryInvitePasswordAsync.inProgress || store.account.changeTemporaryInvitePasswordV2Async.inProgress,
    'custom-attribute': '806-reset-password-set-page'
  }, isAppfolio ? t('Sign up') : t('sign_up_flow.submit_new_password'))), !!localStorage.getItem('INVITED_USER_EMAIL') && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Button, {
    component: RouterLink,
    variant: "text",
    size: "small",
    to: "/login",
    fullWidth: true,
    disabled: store.account.loginAccountAsync.inProgress || store.account.resetAccountPasswordAsync.inProgress || store.account.changeTemporaryPasswordAsync.inProgress || store.account.changeTemporaryInvitePasswordAsync.inProgress || store.account.changeTemporaryInvitePasswordV2Async.inProgress,
    'custom-attribute': '807-reset-password-set-page'
  }, t('sign_up_flow.return_to_the_login_page'))))))));
});
ResetPasswordSetPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};